import { Col, Row, Tabs, Tag } from "antd";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonProps } from "../../helpers/constants";
import { statusColors } from "../../helpers/tickets";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";
import NetwalaButton from "../Items/Button/Index";
import TailSpinLoader from "../Items/Loaders/TailSpinLoader";
import SupportTicketsTable from "../Items/Table/SupportTicketsTable";
import "./support-tickets.css";
import PlusIcon from "./../../assets/svgs/IconsWhite/PlusIcon.svg";

const ViewAllTicketsComponent = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<ITicket[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationProp>({
    currentPage: 1,
    totalPages: 0,
    nextPage: null,
    previousPage: null,
    totalCount: 0,
    pageSize: 50,
  });
  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<ResponseProp>("support/tickets/getTickets");

  useEffect(() => {
    if (apiResponse !== null) {
      const data: any = apiResponse.responseData;
      const {
        currentPage: apiCurrentPage,
        nextPage,
        pageSize: apiPageSize,
        previousPage,
        totalCount,
        totalPages,
        tickets,
      } = data;
      setPagination({
        currentPage: apiCurrentPage,
        totalPages,
        nextPage,
        previousPage,
        totalCount,
        pageSize: apiPageSize,
      });
      setTickets(tickets || []);
      // Don't update state from API response - we control that from user actions
    }
  }, [apiResponse]);

  // Track previous filter values to detect filter changes (not page/pageSize changes)
  const prevFiltersRef = useRef({
    selectedStatus,
  });

  useEffect(() => {
    // Check if filters changed (excluding page and pageSize changes)
    const filtersChanged =
      prevFiltersRef.current.selectedStatus !== selectedStatus;

    // If filters changed, reset to page 1
    if (filtersChanged) {
      setCurrentPage(1);
    }

    const statusToSend = selectedStatus === "ALL" ? null : selectedStatus;
    postApiData({
      page: filtersChanged ? 1 : currentPage,
      status: statusToSend,
      pageSize,
    });

    // Update previous filter values
    prevFiltersRef.current = {
      selectedStatus,
    };
  }, [selectedStatus, pageSize, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // useEffect will handle the API call
  };

  const handlePageSizeChange = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    // useEffect will handle the API call
  };

  const handleTabChange = (key: string) => {
    setSelectedStatus(key);
    setCurrentPage(1);
    // useEffect will handle the API call and reset pagination
  };
  if (apiLoading) {
    return <TailSpinLoader />;
  }
  const tabItems = [
    {
      key: "ALL",
      label: "All",
    },
    {
      key: "OPEN",
      label: "Open",
    },
    {
      key: "ASSIGNED",
      label: "Assigned",
    },
    {
      key: "CLOSED",
      label: "Closed",
    },
  ];

  return (
    <>
      {contextHolder}

      <Row style={{ width: "100%" }} gutter={[16, 16]}>
        <Col span={6} offset={18} style={{ marginBottom: "20px" }}>
          <NetwalaButton
            backgroundColor={ButtonProps.background.STANDARD}
            height={ButtonProps.height.STANDARD}
            width={ButtonProps.width.STANDARD}
            textColor={ButtonProps.color.STANDARD}
            onClick={() => {
              navigate("/create-ticket");
            }}
            text="Create New Complaint"
            icon={<img src={PlusIcon} />}
          />
        </Col>
        <Col span={24} style={{ marginBottom: "20px" }}>
          <Tabs
            activeKey={selectedStatus}
            onChange={handleTabChange}
            items={tabItems.map((item) => {
              const color =
                item.key === "ALL"
                  ? undefined
                  : statusColors[item.key as keyof typeof statusColors];
              return {
                key: item.key,
                label: (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {item.label}
                    {color && (
                      <Tag
                        color={color}
                        style={{
                          margin: 0,
                          borderRadius: "4px",
                          padding: "0 6px",
                          fontSize: "10px",
                          lineHeight: "16px",
                          height: "16px",
                        }}
                      />
                    )}
                  </span>
                ),
              };
            })}
          />
        </Col>
        <Col span={24}>
          <SupportTicketsTable
            tickets={tickets}
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
          />
        </Col>
      </Row>
    </>
  );
};
export default ViewAllTicketsComponent;
